import { computed, type Component } from 'vue';
import { useModal } from 'vue-final-modal';

export default function useTableFilterModal<TFilters = any>(
  filterModalComponent: Component,
  _filterStore: { clearFilters: () => void; filters: any },
  onFilterUpdate: (filters: TFilters) => void,
  getCurrentFilters?: () => TFilters,
  additionalProps?: Record<string, any> | (() => Record<string, any>)
) {
  const resolveAdditionalProps = () => {
    const resolved = typeof additionalProps === 'function' ? additionalProps() : additionalProps;
    const { onClearFilters, closeOnClear = true, ...restAdditionalProps } = resolved ?? {};
    return { onClearFilters, closeOnClear, restAdditionalProps };
  };

  const handleClearFilters = () => {
    const { onClearFilters, closeOnClear } = resolveAdditionalProps();
    if (typeof onClearFilters === 'function') {
      onClearFilters();
    } else {
      onFilterUpdate({} as TFilters);
    }
    if (closeOnClear) {
      modal.close();
    }
  };

  const isFilterActive = computed(() => {
    if (!getCurrentFilters) return false;

    const currentFilters = getCurrentFilters();
    return (
      currentFilters &&
      Object.keys(currentFilters).length > 0 &&
      Object.values(currentFilters).some((value) => {
        if (typeof value === 'boolean') {
          return true;
        }
        if (typeof value === 'number') {
          return Number.isFinite(value);
        }
        return value !== null && value !== undefined && value !== '';
      })
    );
  });

  const modal = useModal({
    component: filterModalComponent,
    attrs: {
      onClose: () => modal.close(),
      currentFilters: getCurrentFilters ? getCurrentFilters() : {},
      'onUpdate:filter': (filters: TFilters) => {
        onFilterUpdate(filters);
        modal.close();
      },
      onClearFilters: handleClearFilters,
      ...resolveAdditionalProps().restAdditionalProps,
    },
  });

  const openFilterModal = () => {
    modal.patchOptions({
      attrs: {
        ...modal.options.attrs,
        ...(getCurrentFilters ? { currentFilters: getCurrentFilters() } : {}),
        ...resolveAdditionalProps().restAdditionalProps,
        onClearFilters: handleClearFilters,
      },
    });
    modal.open();
  };

  const closeFilterModal = () => {
    modal.close();
  };

  return {
    isFilterActive,
    openFilterModal,
    closeFilterModal,
    modal,
  };
}
